import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import EMatrixLogo from '../components/EMatrixLogo';
import { APP_VERSION, APP_BUILD_DATE } from '../version';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Willkommen, ${user.name}!`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <EMatrixLogo size="large" />
        </div>
        
        <p className="text-center mb-8" style={{ color: '#5A7A9B' }}>
          Service Portal
        </p>

        <Card className="login-card border-0">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold" style={{ color: '#0D2440' }}>
              Anmelden
            </CardTitle>
            <CardDescription style={{ color: '#5A7A9B' }}>
              Geben Sie Ihre Anmeldedaten ein
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" style={{ color: '#0D2440' }}>E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@firma.at"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  data-testid="login-email-input"
                  className="border-[#C8D8E8] focus:border-[#2E5E99] focus:ring-[#2E5E99]"
                  style={{ background: 'white' }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" style={{ color: '#0D2440' }}>Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  data-testid="login-password-input"
                  className="border-[#C8D8E8] focus:border-[#2E5E99] focus:ring-[#2E5E99]"
                  style={{ background: 'white' }}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
                data-testid="login-submit-button"
                style={{ background: '#0D2440', color: 'white' }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Anmeldung...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Anmelden
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm mt-6" style={{ color: '#5A7A9B' }}>
          Kontaktieren Sie e-Matrix für Zugangsdaten
        </p>

        {/* Version Info */}
        <p className="text-center text-xs mt-4" style={{ color: '#9BB0C4' }} data-testid="version-info">
          v{APP_VERSION} ({APP_BUILD_DATE})
        </p>
      </div>
    </div>
  );
};

export default Login;
